/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useContext, useState } from "react"
import { observer } from "mobx-react-lite"
import { useSnackbar } from "notistack"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import VirtualExamContext from "views/exam/virtual-exam/virtual-exam-context"
import BookedPatientDetails from "views/exam/virtual-exam/exam-room/components/booked-patient-details"
import { logError } from "@medaica/common/services/util"

/**
 * Takes the auscultation list's place in a booked exam until the technician confirms that the person who joined is
 * the booked patient, so no recording starts before that. As wide as the list it replaces.
 */
const IdentityConfirmation = observer(({ widthPx }: { widthPx: number }): ReactElement => {
  const { virtualExamStore, avStore } = useContext(VirtualExamContext)
  const { enqueueSnackbar } = useSnackbar()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleYesClicked = async () => {
    setIsSaving(true)
    try {
      await virtualExamStore.affirmPatientIdentity()
      setIsDialogOpen(false)
    } catch (error) {
      logError(error)
      enqueueSnackbar("The confirmation could not be saved. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="relative flex-shrink-0" style={{ width: widthPx }}>
      <div className="absolute inset-0 panel bg-white py-4 px-4 overflow-y-auto">
        <h3 className="panel-title mb-5">Confirm patient identity</h3>
        <p>
          Before starting any auscultations, confirm that the person who joined this exam is the patient it was booked
          for.
        </p>
        <div className="mt-4">
          <BookedPatientDetails stacked />
        </div>
        {!avStore.remoteParticipant && <p className="mt-3 text-gray-500">Waiting for the patient to join.</p>}
        <Button
          variant="contained"
          size="small"
          disabled={!avStore.remoteParticipant}
          onClick={() => setIsDialogOpen(true)}
          sx={{ mt: 3 }}
        >
          Confirm identity...
        </Button>
      </div>
      <Dialog open={isDialogOpen} onClose={() => !isSaving && setIsDialogOpen(false)}>
        <DialogTitle>Confirm patient identity</DialogTitle>
        <DialogContent>
          Confirm the patient joining this exam matches the name, date of birth, and phone number we have on file.
        </DialogContent>
        <DialogActions>
          <Button disabled={isSaving} onClick={() => setIsDialogOpen(false)}>
            No
          </Button>
          <Button disabled={isSaving} onClick={() => void handleYesClicked()}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
})

export default IdentityConfirmation
